
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronLeft, ChevronRight, Plus, Send, Clock, CheckCircle, Trash2, Pencil, Lock, FileText, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import RichTextDisplay from '@/components/RichTextDisplay';
import { formatDate, formatShortDate, getDayName, getPayPeriodDates, statusColors } from './timesheets/utils';
import { EntryFormSheet } from './timesheets/EntryFormSheet';
import { ReviewSheet } from './timesheets/ReviewSheet';

export default function TimesheetsPage() {
  const { user, hasPerm } = useAuth();
  // Manager status now derived from the granular approve_timesheets permission
  // (Admin/Owner always get it via role default; OM toggleable; Employee=false).
  const isManager = !!hasPerm('approve_timesheets');

  const [payPeriod, setPayPeriod] = useState({ start: '', end: '' });
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [rfps, setRfps] = useState([]);
  const [showClosedProposals, setShowClosedProposals] = useState(false);
  const [projectSections, setProjectSections] = useState({});

  // Entry form
  const [entryFormOpen, setEntryFormOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    date: '', project_id: '', proposal_id: '', lead_id: '', rfp_id: '',
    contract_section_id: '', hours: '', description: '', is_billable: true
  });

  const [approvalQueue, setApprovalQueue] = useState([]);
  const [reviewSheet, setReviewSheet] = useState(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [periodLocked, setPeriodLocked] = useState(null);

  const periodDates = useMemo(() => {
    if (!payPeriod.start || !payPeriod.end) return [];
    return getPayPeriodDates(payPeriod.start, payPeriod.end);
  }, [payPeriod]);

  const fetchPayPeriod = useCallback(async () => {
    const r = await api.get('/pay-periods/current');
    setPayPeriod(r.data);
  }, []);

  const fetchEntries = useCallback(async () => {
    if (!payPeriod.start) return;
    const r = await api.get('/timesheets', { params: { user_id: user.id, pay_period_start: payPeriod.start } });
    setEntries(r.data);
  }, [user?.id, payPeriod.start]);

  const fetchApproval = useCallback(async () => {
    if (!isManager) return;
    try {
      const r = await api.get('/timesheets/approval-queue');
      setApprovalQueue(r.data);
    } catch { /* not manager */ }
  }, [isManager]);

  useEffect(() => {
    fetchPayPeriod();
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/proposals').then(r => setProposals(r.data)).catch(() => {});
    api.get('/leads').then(r => setLeads(r.data || [])).catch(() => {});
    api.get('/rfp-opportunities').then(r => setRfps(r.data || [])).catch(() => {});
  }, [fetchPayPeriod]);

  useEffect(() => {
    if (payPeriod.start) {
      fetchEntries();
      fetchApproval();
      api.get('/payroll-periods/lock-status', { params: { pay_period_start: payPeriod.start } })
        .then(r => setPeriodLocked(r.data.locked ? r.data : null))
        .catch(() => setPeriodLocked(null));
    }
  }, [payPeriod.start, fetchEntries, fetchApproval]);

  const loadSections = async (projectId) => {
    if (!projectId || projectSections[projectId]) return;
    try {
      const r = await api.get(`/projects/${projectId}/sections`);
      setProjectSections(prev => ({ ...prev, [projectId]: r.data.filter(s => !s.is_default) }));
    } catch { /* best-effort fetch — silent fail */ }
  };

  const navigatePeriod = (dir) => {
    if (!payPeriod.start) return;
    const d = new Date(payPeriod.start + 'T12:00:00');
    if (isNaN(d.getTime())) return;
    d.setDate(d.getDate() + (dir * 14));
    setPayPeriod({ start: formatDate(d), end: formatDate(new Date(d.getTime() + 13 * 86400000)) });
  };

  // Group entries by their linkage (project+section / proposal / lead / RFP) for the grid
  const gridRows = useMemo(() => {
    const rows = {};
    entries.forEach(e => {
      let key;
      if (e.proposal_id) key = `proposal__${e.proposal_id}`;
      else if (e.lead_id) key = `lead__${e.lead_id}`;
      else if (e.rfp_id) key = `rfp__${e.rfp_id}`;
      else key = `${e.project_id || 'none'}__${e.contract_section_id || 'none'}`;
      if (!rows[key]) {
        rows[key] = {
          project_id: e.project_id,
          proposal_id: e.proposal_id,
          lead_id: e.lead_id,
          rfp_id: e.rfp_id,
          contract_section_id: e.contract_section_id,
          project_name: (e.proposal_id || e.lead_id || e.rfp_id) ? '' : (e.project_name || 'No Project'),
          proposal_number: e.proposal_number || '',
          lead_label: e.lead_label || '',
          rfp_label: e.rfp_label || '',
          section_name: e.section_name || '',
          days: {},
          totalHours: 0,
        };
      }
      rows[key].days[e.date] = rows[key].days[e.date] || [];
      rows[key].days[e.date].push(e);
      rows[key].totalHours += e.hours;
    });
    return Object.values(rows);
  }, [entries]);

  const totalPeriodHours = entries.reduce((sum, e) => sum + e.hours, 0);
  const dailyTotals = periodDates.map(d => entries.filter(e => e.date === d).reduce((s, e) => s + e.hours, 0));

  const periodStatus = useMemo(() => {
    if (entries.length === 0) return 'empty';
    if (entries.every(e => e.status === 'Approved')) return 'Approved';
    if (entries.some(e => e.status === 'Submitted')) return 'Submitted';
    if (entries.some(e => e.status === 'Rejected')) return 'Rejected';
    return 'Draft';
  }, [entries]);

  // Entry form handlers
  const openNewEntry = (date) => {
    setEditEntry(null);
    // Default date = today, unless an explicit date was clicked. Clamp to
    // the visible pay period so the entry actually lands inside the grid
    // the user is currently looking at.
    const todayIso = new Date().toISOString().split('T')[0];
    let defaultDate = date || todayIso;
    if (!date && payPeriod.start && payPeriod.end) {
      if (todayIso < payPeriod.start) defaultDate = payPeriod.start;
      else if (todayIso > payPeriod.end) defaultDate = payPeriod.end;
    }
    setEntryForm({
      date: defaultDate,
      project_id: '', proposal_id: '', lead_id: '', rfp_id: '',
      contract_section_id: '', hours: '', description: '', is_billable: true
    });
    setShowClosedProposals(false);
    setEntryFormOpen(true);
  };

  const openEditEntry = (entry) => {
    setEditEntry(entry);
    setEntryForm({
      date: entry.date,
      project_id: entry.project_id || '',
      proposal_id: entry.proposal_id || '',
      lead_id: entry.lead_id || '',
      rfp_id: entry.rfp_id || '',
      contract_section_id: entry.contract_section_id || '',
      hours: entry.hours, description: entry.description || '',
      is_billable: entry.is_billable !== false
    });
    if (entry.project_id) loadSections(entry.project_id);
    setEntryFormOpen(true);
  };

  const handleProjectChange = (pid) => {
    setEntryForm(f => ({ ...f, project_id: pid, proposal_id: '', lead_id: '', rfp_id: '', contract_section_id: '' }));
    if (pid) loadSections(pid);
  };

  const handleProposalChange = (propId) => {
    setEntryForm(f => ({ ...f, proposal_id: propId, project_id: '', lead_id: '', rfp_id: '', contract_section_id: '' }));
  };

  const handleLeadChange = (leadId) => {
    setEntryForm(f => ({ ...f, lead_id: leadId, project_id: '', proposal_id: '', rfp_id: '', contract_section_id: '' }));
  };

  const handleRfpChange = (rfpId) => {
    setEntryForm(f => ({ ...f, rfp_id: rfpId, project_id: '', proposal_id: '', lead_id: '', contract_section_id: '' }));
  };

  const filteredProposals = proposals.filter(p =>
    showClosedProposals ? true : (p.status !== 'Closed - Won' && p.status !== 'Closed - Lost')
  );

  const saveEntry = async () => {
    if (!entryForm.date) return toast.error('Date is required');
    if (entryForm.date > new Date().toISOString().split('T')[0]) return toast.error('Cannot log time for a future date');
    const hours = parseFloat(entryForm.hours);
    if (!hours || hours <= 0) return toast.error('Hours must be greater than 0');
    if (!entryForm.description?.trim()) return toast.error('Notes/description is required');
    const linked = [entryForm.project_id, entryForm.proposal_id, entryForm.lead_id, entryForm.rfp_id].filter(Boolean);
    if (linked.length === 0) return toast.error('A Project, Proposal, Lead, or RFP is required. If this isn\'t for a client project, please select an internal job project.');
    if (linked.length > 1) return toast.error('An entry can only be linked to one of Project, Proposal, Lead, or RFP');
    if (entryForm.project_id && !entryForm.contract_section_id) return toast.error('Section is required when a project is selected');
    if (hours > 12) {
      if (!window.confirm(`You are logging ${hours} hours for this day. Continue?`)) return;
    }
    try {
      const payload = { ...entryForm, hours };
      if (editEntry) {
        await api.put(`/timesheets/${editEntry.id}`, payload);
        toast.success('Entry updated');
      } else {
        await api.post('/timesheets', payload);
        toast.success('Hours logged');
      }
      setEntryFormOpen(false);
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving entry'); }
  };

  const deleteEntry = async (eid) => {
    try {
      await api.delete(`/timesheets/${eid}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting'); }
  };

  const submitPeriod = async () => {
    const draftCount = entries.filter(e => e.status === 'Draft').length;
    if (draftCount === 0) return toast.error('No draft entries to submit');
    try {
      const weekStarts = [...new Set(entries.filter(e => e.status === 'Draft').map(e => e.week_start))];
      let totalSubmitted = 0;
      for (const ws of weekStarts) {
        const r = await api.post('/timesheets/submit-week', { week_start: ws });
        totalSubmitted += r.data.submitted;
      }
      toast.success(`${totalSubmitted} entries submitted`);
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error submitting'); }
  };

  // Approval handlers
  const approveWeek = async (item) => {
    try {
      await api.post('/timesheets/approve-week', { user_id: item.user_id, week_start: item.week_start });
      toast.success(`Approved ${item.user_name}'s timesheet`);
      setReviewSheet(null);
      fetchApproval();
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error approving'); }
  };

  const rejectWeek = async (item) => {
    if (!rejectionNote.trim()) return toast.error('Rejection note is required');
    try {
      await api.post('/timesheets/reject-week', { user_id: item.user_id, week_start: item.week_start, rejection_note: rejectionNote });
      toast.success(`Rejected ${item.user_name}'s timesheet`);
      setRejectionNote('');
      setReviewSheet(null);
      fetchApproval();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error rejecting'); }
  };

  const currentSections = entryForm.project_id ? (projectSections[entryForm.project_id] || []) : [];

  return (
    <div className="space-y-4 animate-fade-in" data-testid="timesheets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-medium tracking-tight">Timesheets</h1>
        </div>
      </div>

      <Tabs defaultValue="my-time">
        <TabsList>
          <TabsTrigger value="my-time" className="text-xs" data-testid="tab-my-time">
            <Clock className="h-3 w-3 mr-1" /> My Time
          </TabsTrigger>
          {isManager && (
            <TabsTrigger value="approval" className="text-xs" data-testid="tab-approval">
              <CheckCircle className="h-3 w-3 mr-1" /> Approval Queue
              {approvalQueue.length > 0 && (
                <Badge className="ml-1 h-4 min-w-4 rounded-full bg-primary text-xs px-1">{approvalQueue.length}</Badge>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* ───── MY TIME TAB ───── */}
        <TabsContent value="my-time" className="mt-4 space-y-4">
          {/* Pay period navigator */}
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriod(-1)} data-testid="prev-week">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-sm font-medium">
              Pay Period: {formatShortDate(payPeriod.start)} - {formatShortDate(payPeriod.end)}
            </div>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigatePeriod(1)} data-testid="next-week">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline" size="sm" className="text-xs ml-2"
              onClick={fetchPayPeriod}
            >
              Current
            </Button>
            <div className="ml-auto flex items-center gap-2">
              <Badge className={statusColors[periodStatus] || 'bg-muted text-muted-foreground'}>
                {periodStatus === 'empty' ? 'No Entries' : periodStatus}
              </Badge>
              <span className="text-xs text-muted-foreground">{entries.length} entries</span>
              <span className="text-sm font-heading font-bold tabular-nums">{totalPeriodHours.toFixed(1)}h total</span>
            </div>
          </div>

          {/* Payroll Period Lock Banner */}
          {periodLocked && (
            <div className="flex items-center gap-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/20" data-testid="payroll-lock-banner">
              <Lock className="h-5 w-5 text-amber-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-400">Payroll Period Locked</p>
                <p className="text-xs text-amber-400/80">
                  This pay period is in <span className="font-semibold">{periodLocked.stage}</span> status. New timesheet entries cannot be created or submitted until the period is reopened.
                </p>
              </div>
            </div>
          )}

          {/* Pay period grid */}
          <div className="border border-border rounded-md bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground min-w-[180px] sticky left-0 bg-card z-10">Project / Section</TableHead>
                  {periodDates.map(d => (
                    <TableHead key={d} className="text-xs uppercase tracking-wider text-muted-foreground text-center min-w-[58px] px-1">
                      <div className="text-xs">{getDayName(d)}</div>
                      <div className="text-xs font-normal">{formatShortDate(d)}</div>
                    </TableHead>
                  ))}
                  <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-center min-w-[55px]">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gridRows.map((row, ri) => (
                  <TableRow key={ri} className="border-border hover:bg-secondary/20">
                    <TableCell className="py-2 sticky left-0 bg-card z-10">
                      {row.proposal_id ? (
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3 w-3 text-amber-400 shrink-0" />
                          <p className="text-xs font-medium truncate text-amber-400">Proposal {row.proposal_number}</p>
                        </div>
                      ) : row.lead_id ? (
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3 w-3 text-sky-400 shrink-0" />
                          <p className="text-xs font-medium truncate text-sky-400">Lead: {row.lead_label}</p>
                        </div>
                      ) : row.rfp_id ? (
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3 w-3 text-purple-400 shrink-0" />
                          <p className="text-xs font-medium truncate text-purple-400">RFP: {row.rfp_label}</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5">
                            <Briefcase className="h-3 w-3 text-primary shrink-0" />
                            <p className="text-xs font-medium truncate">{row.project_name}</p>
                          </div>
                          {row.section_name && <p className="text-xs text-muted-foreground truncate ml-[18px]">{row.section_name}</p>}
                        </>
                      )}
                    </TableCell>
                    {periodDates.map(d => {
                      const dayEntries = row.days[d] || [];
                      const dayHours = dayEntries.reduce((s, e) => s + e.hours, 0);
                      return (
                        <TableCell key={d} className="text-center py-2">
                          {dayHours > 0 ? (
                            <button
                              className="text-sm font-medium text-primary hover:underline cursor-pointer tabular-nums"
                              onClick={() => dayEntries[0] && openEditEntry(dayEntries[0])}
                              data-testid={`cell-${ri}-${d}`}
                            >
                              {dayHours.toFixed(1)}
                            </button>
                          ) : (
                            <span className="text-xs text-muted-foreground/30">-</span>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-center text-sm font-heading font-bold py-2 tabular-nums">{row.totalHours.toFixed(1)}</TableCell>
                  </TableRow>
                ))}
                {gridRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={periodDates.length + 2} className="text-center text-sm text-muted-foreground py-8">
                      No hours logged this pay period. Click &quot;Log Hours&quot; to get started.
                    </TableCell>
                  </TableRow>
                )}
                {/* Totals row */}
                {gridRows.length > 0 && (
                  <TableRow className="border-border bg-secondary/30 font-bold">
                    <TableCell className="text-xs uppercase sticky left-0 bg-secondary/30 z-10">Daily Total</TableCell>
                    {dailyTotals.map((h, i) => (
                      <TableCell key={i} className={`text-center text-xs tabular-nums ${h > 12 ? 'text-destructive' : ''}`}>
                        {h > 0 ? h.toFixed(1) : '-'}
                      </TableCell>
                    ))}
                    <TableCell className="text-center text-sm font-heading tabular-nums">{totalPeriodHours.toFixed(1)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <Button onClick={() => openNewEntry()} className="bg-primary text-primary-foreground" disabled={!!periodLocked} data-testid="log-hours-btn">
              <Plus className="h-4 w-4 mr-1" /> Log Hours
            </Button>
            {entries.some(e => e.status === 'Draft') && !periodLocked && (
              <Button onClick={submitPeriod} variant="outline" className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10" data-testid="submit-week-btn">
                <Send className="h-4 w-4 mr-1" /> Submit Period
              </Button>
            )}
            {periodLocked && entries.some(e => e.status === 'Draft' && e.rejection_note) && (
              <Button onClick={submitPeriod} variant="outline" className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10" data-testid="resubmit-rejected-btn">
                <Send className="h-4 w-4 mr-1" /> Resubmit Rejected Entries
              </Button>
            )}
          </div>

          {/* Day-by-day detail list */}
          {entries.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Entries This Period</h3>
              {[...entries].sort((a, b) => {
                const order = { Draft: 0, Submitted: 1, Approved: 2 };
                return (order[a.status] ?? 3) - (order[b.status] ?? 3);
              }).map(e => (
                <Card key={e.id} className="border-border bg-background shadow-none">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-muted-foreground">{formatShortDate(e.date)}</span>
                          <span className="text-sm font-bold text-primary tabular-nums">{e.hours}h</span>
                          <Badge status={e.status} className={`text-xs ${statusColors[e.status]}`}>{e.status}</Badge>
                          {!e.is_billable && <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {e.proposal_id
                            ? <span className="text-amber-400">Proposal {e.proposal_number || e.proposal_id}</span>
                            : e.lead_id
                              ? <span className="text-sky-400">Lead: {e.lead_label || e.lead_id}</span>
                              : e.rfp_id
                                ? <span className="text-purple-400">RFP: {e.rfp_label || e.rfp_id}</span>
                                : <>{e.project_name || 'No Project'}{e.section_name ? ` / ${e.section_name}` : ''}</>
                          }
                        </p>
                        {e.description && <div className="mt-0.5"><RichTextDisplay value={e.description} collapsedHeight={20} /></div>}
                        {e.rejection_note && <p className="text-xs text-destructive mt-0.5">Rejected: {e.rejection_note}</p>}
                      </div>
                      {e.status === 'Draft' && (
                        <div className="flex items-center gap-1 ml-2">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditEntry(e)} data-testid={`edit-entry-${e.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteEntry(e.id)} data-testid={`delete-entry-${e.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ───── APPROVAL TAB ───── */}
        {isManager && (
          <TabsContent value="approval" className="mt-4 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-muted-foreground">Pending Approval ({approvalQueue.length})</h2>
              {approvalQueue.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  onClick={async () => {
                    const hrs = approvalQueue.reduce((s, i) => s + (i.total_hours || 0), 0);
                    if (!window.confirm(`Approve all ${approvalQueue.length} pending timesheet(s) (${hrs.toFixed(1)}h total)?`)) return;
                    try {
                      // Determine date range from the approval queue weeks
                      const weeks = approvalQueue.map(i => i.week_start).sort();
                      const start = weeks[0];
                      const endWeek = new Date(weeks[weeks.length - 1] + 'T00:00:00');
                      endWeek.setDate(endWeek.getDate() + 6);
                      const end = endWeek.toISOString().slice(0, 10);
                      const r = await api.post('/timesheets/mass-approve', { pay_period_start: start, pay_period_end: end });
                      toast.success(`Approved ${r.data.approved} timesheet entries`);
                      fetchApproval();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Mass approve failed');
                    }
                  }}
                  data-testid="mass-approve-timesheets-btn"
                >
                  <CheckCircle className="h-3 w-3 mr-1" /> Approve All Pending
                </Button>
              )}
            </div>
            {approvalQueue.length === 0 ? (
              <Card className="border-border bg-background shadow-none">
                <CardContent className="p-8 text-center text-sm text-muted-foreground">
                  No timesheets pending approval.
                </CardContent>
              </Card>
            ) : (
              approvalQueue.map((item, i) => (
                <Card key={i} className="border-border bg-background shadow-none cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => setReviewSheet(item)} data-testid={`approval-item-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{item.user_name}</p>
                        <p className="text-xs text-muted-foreground">{item.user_email}</p>
                        <p className="text-xs text-muted-foreground mt-1">Week of {formatShortDate(item.week_start)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-heading font-bold text-primary tabular-nums">{item.total_hours.toFixed(1)}h</p>
                        <p className="text-xs text-muted-foreground">{item.entry_count} entries</p>
                        <Badge className={statusColors.Submitted}>Submitted</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* ───── LOG / EDIT ENTRY SHEET ───── */}
      <EntryFormSheet
        open={entryFormOpen}
        onOpenChange={setEntryFormOpen}
        editEntry={editEntry}
        entryForm={entryForm}
        setEntryForm={setEntryForm}
        projects={projects}
        proposals={proposals}
        filteredProposals={filteredProposals}
        leads={leads}
        rfps={rfps}
        currentSections={currentSections}
        showClosedProposals={showClosedProposals}
        setShowClosedProposals={setShowClosedProposals}
        handleProjectChange={handleProjectChange}
        handleProposalChange={handleProposalChange}
        handleLeadChange={handleLeadChange}
        handleRfpChange={handleRfpChange}
        saveEntry={saveEntry}
      />

      {/* ───── REVIEW SHEET ───── */}
      <ReviewSheet
        reviewSheet={reviewSheet}
        onClose={() => setReviewSheet(null)}
        user={user}
        approveWeek={approveWeek}
        rejectWeek={rejectWeek}
        rejectionNote={rejectionNote}
        setRejectionNote={setRejectionNote}
      />
    </div>
  );
}
